import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Tag, PageHeader, Button, Drawer, Space, BackTop, Table, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusOutlined, ImportOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import EditVehicleForm from '../containers/EditVehicleForm';
import { useGetVehiclesQuery } from '../../../app/api/vehicleApiSlice';
import { formatDateTime, formatCreatedDate } from '../../../utils/dateTimeUtil';
import VehicleStatus from '../../../core/enum/VehicleStatus';
import Vehicle from '../../../core/models/Vehicle';
import VehicleStatusColorCode from '../../../core/enum/VehicleStatusColorCode';
import tableColumnTextFilterConfig from '../../../utils/searchUtil';
import {
  getVehicleColors,
  getVehicleMakes,
  getVehicleModels,
} from '../../../app/store/commonSlice';
import ImportVehicleForm from '../containers/ImportVehicleForm';

const ListVehicles: React.FC = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const view = searchParams.get('view') ?? 'approved';

  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  const [importDrawerVisible, setImportDrawerVisible] = useState(false);

  const {
    data: vehicles,
    isLoading,
    isFetching,
    refetch,
  } = useGetVehiclesQuery(undefined, {
    selectFromResult: ({ data, isLoading, isFetching }) => {
      if (view === 'new') {
        const newVehicles = data?.filter(
          (vehicle) =>
            vehicle.status === VehicleStatus.New ||
            vehicle.status === VehicleStatus.Registered ||
            vehicle.status === VehicleStatus.Rejected,
        );
        return { data: newVehicles, isLoading, isFetching };
      } else if (view === 'approved') {
        const approvedVehicles = data?.filter(
          (vehicle) =>
            vehicle.status === VehicleStatus.Approved ||
            vehicle.status === VehicleStatus.GradingCompleted ||
            vehicle.status === VehicleStatus.PriceListed ||
            vehicle.status === VehicleStatus.AuctionReady,
        );
        return { data: approvedVehicles, isLoading, isFetching };
      } else if (view === 'auctioned') {
        const approvedVehicles = data?.filter(
          (vehicle) =>
            vehicle.status === VehicleStatus.Sold ||
            vehicle.status === VehicleStatus.Unsold ||
            vehicle.status === VehicleStatus.Delivered ||
            vehicle.status === VehicleStatus.DeliveryReady ||
            vehicle.status === VehicleStatus.Withdrawn,
        );
        return { data: approvedVehicles, isLoading, isFetching };
      }
      return { data, isLoading, isFetching };
    },
  });

  const vehicleMakes = useSelector(getVehicleMakes);
  const vehicleModels = useSelector(getVehicleModels);
  const vehicleColors = useSelector(getVehicleColors);

  const statusFilter = Object.values(VehicleStatus)
    .filter((value) => isNaN(Number(value)))
    .map((status) => ({
      text: status.toString(),
      value: status.toString(),
    }));

  const columns: ColumnsType<Vehicle> = [
    {
      title: 'VID',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: (text: string) => text,
      ...tableColumnTextFilterConfig<Vehicle>(),
      onFilter: (value, record) => {
        return record.code.toString().toLowerCase().includes(value.toString().toLowerCase());
      },
    },
    {
      title: 'Reg. No.',
      dataIndex: 'plateNumber',
      key: 'plateNumber',
      sorter: (a, b) => a.plateNumber.localeCompare(b.plateNumber),
      render: (text: string) => text,
      ...tableColumnTextFilterConfig<Vehicle>(),
      onFilter: (value, record) => {
        return record.plateNumber.toString().toLowerCase().includes(value.toString().toLowerCase());
      },
    },
    {
      title: 'Make',
      dataIndex: 'make',
      key: 'make',
      render: (value: number) => vehicleMakes?.find((make) => make.id === value)?.name,
      sorter: (a, b) => a.make - b.make,
      filters: vehicleMakes?.map((make) => ({ text: make.name, value: make.id })),
      filterSearch: true,
      onFilter: (value, record) => {
        return record.make === value;
      },
      responsive: ['md'],
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (value: number) => vehicleModels?.find((model) => model.id === value)?.name,
      sorter: (a, b) => a.model - b.model,
      filters: vehicleModels?.map((model) => ({ text: model.name, value: model.id })),
      filterSearch: true,
      onFilter: (value, record) => {
        return record.model === value;
      },
      responsive: ['md'],
    },
    {
      title: 'YoM',
      dataIndex: 'yom',
      key: 'yom',
      sorter: (a, b) => a.yom - b.yom,
      render: (text: string) => text,
      ...tableColumnTextFilterConfig<Vehicle>(),
      onFilter: (value, record) => {
        return record.yom.toString().toLowerCase().includes(value.toString().toLowerCase());
      },
      responsive: ['md'],
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      render: (value: number) => vehicleColors?.find((color) => color.id === value)?.name,
      sorter: (a, b) => a.color - b.color,
      filters: vehicleColors?.map((color) => ({ text: color.name, value: color.id })),
      filterSearch: true,
      onFilter: (value, record) => {
        return record.color === value;
      },
      responsive: ['xl'],
    },
    {
      title: 'Mileage',
      dataIndex: 'mileage',
      key: 'mileage',
      sorter: (a, b) => a.mileage - b.mileage,
      render: (mileage: number) => <>{mileage ? mileage.toLocaleString() : '-'} km</>,
      ...tableColumnTextFilterConfig<Vehicle>(),
      onFilter: (value, record) => {
        return record.mileage?.toString().toLowerCase().includes(value?.toString().toLowerCase());
      },
      responsive: ['xl'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: VehicleStatus) => (
        <Tag color={VehicleStatusColorCode[status]}>{VehicleStatus[status]}</Tag>
      ),
      sorter: (a, b) => a.status - b.status,
      filters: statusFilter,
      onFilter: (value, record) => {
        return VehicleStatus[record.status] === value;
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (date: Date) => {
        return <>{formatCreatedDate(date)}</>;
      },
      sorter: (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
      // defaultSortOrder: 'descend',
    },
    {
      title: 'Updated Date',
      dataIndex: 'updatedDate',
      key: 'updatedDate',
      render: (date: Date) => {
        return <>{formatDateTime(date)}</>;
      },
      sorter: (a, b) => new Date(a.updatedDate).getTime() - new Date(b.updatedDate).getTime(),
      defaultSortOrder: 'descend',
    },
  ];

  return (
    <>
      <BackTop />
      <PageHeader
        className='site-page-header'
        title='My Vehicles'
        extra={[
          <Button
            key='importVehicle'
            type='default'
            icon={<ImportOutlined />}
            onClick={() => {
              setImportDrawerVisible(true);
            }}
          >
            Import Vehicles
          </Button>,
          <Button
            key='addVehicle'
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => {
              setAddDrawerVisible(true);
            }}
          >
            Add New Vehicle
          </Button>,
        ]}
      />
      <Table
        dataSource={vehicles}
        columns={columns}
        rowKey='id'
        loading={isFetching}
        size='small'
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {
              navigate(`/vehicles/${record.id}`);
            },
          };
        }}
      />
      <Drawer
        title='Add New Vehicle'
        width={600}
        onClose={() => setAddDrawerVisible(false)}
        open={addDrawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
        destroyOnClose={true}
        extra={
          <Space>
            <Button onClick={() => setAddDrawerVisible(false)}>Cancel</Button>
            <Button type='primary' form='updateVehicleForm' key='submit' htmlType='submit'>
              Submit
            </Button>
          </Space>
        }
      >
        <EditVehicleForm
          vehicle={null}
          onSuccessfulSubmit={() => setAddDrawerVisible(false)}
          drawerVisible={addDrawerVisible}
        />
      </Drawer>
      <Drawer
        title='Import Vehicles'
        width={600}
        onClose={() => setImportDrawerVisible(false)}
        open={importDrawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
        destroyOnClose={true}
      >
        <ImportVehicleForm
          onSuccessfulSubmit={() => {
            setImportDrawerVisible(false);
            refetch()
              .then(() => console.log('Refetching...'))
              .catch((error) => console.error('Error refetching', error));
          }}
          drawerVisible={importDrawerVisible}
        />
      </Drawer>
    </>
  );
};

export default ListVehicles;
